import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';
import { PhotosApiService, PhotoState, PortalPhotoSelection } from '@core/api/services/photos-api.service';
import { AuthenticatedPhotoDirective } from '@shared/components/entity-photos/authenticated-photo.directive';
import { UnitSelectorComponent } from '@shared/components/unit-selector/unit-selector.component';
import { feedbackError } from '@core/api/services/feedback-api.service';
import type { PhotoCropPreviewResult } from '@shared/components/entity-photos/photo-crop-dialog.component';

export type { PortalPhotoSelection } from '@core/api/services/photos-api.service';
interface PortalPhotoPreview { id: string; reference: string; isUnit: boolean; role: string; error?: string; thumbnailUrl?: string; personId?: string; consumed: boolean; }
interface PhotoRow extends PortalPhotoPreview { selection: PortalPhotoSelection; state?: PhotoState; loading?: boolean; inferredUnitId?: string; destinationError?: string; destinationVersion?: number; croppedThumbnailUrl?: string; }
@Component({ selector: 'app-portal-photo-import', standalone: true, imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, AuthenticatedPhotoDirective, UnitSelectorComponent],
  templateUrl: './portal-photo-import.component.html', styleUrl: './portal-photo-import.component.scss' })
export class PortalPhotoImportComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) batchId = '';
  @Input() unitDestinations: Record<string, string> = {};
  @Input() includedMemberReferences: readonly string[] | null = null;
  @Input() members: readonly { portalMemberId: string; fullName: string }[] = [];
  @Input() unitOptions: readonly { id: string; name: string }[] = [];
  @Output() selectionsChange = new EventEmitter<PortalPhotoSelection[]>();
  @Output() loadingChange = new EventEmitter<boolean>();
  private readonly http = inject(HttpClient);
  private readonly api = inject(PhotosApiService);
  private readonly dialog = inject(MatDialog);
  readonly rows = signal<PhotoRow[]>([]);
  readonly error = signal('');
  readonly activePhotoId = signal<string | null>(null);
  readonly activePhoto = computed(() => this.rows().find(row => row.id === this.activePhotoId()));
  get selectedPhotoCount() { return this.rows().filter(row => row.selection.include && this.memberIncluded(row) && !row.error && !row.consumed && !row.loading).length; }
  get photoGroups() {
    return [{ key: 'communities', name: 'Comunidades', isUnit: true, rows: this.rows().filter(row => row.isUnit) },
      { key: 'members', name: 'Miembros', isUnit: false, rows: this.rows().filter(row => !row.isUnit) }].filter(group => group.rows.length);
  }
  trackGroup(_: number, group: { key: string }) { return group.key; }
  trackPhoto(_: number, row: PhotoRow) { return row.id; }
  photoTitle(row: PhotoRow) { return row.isUnit ? row.role : this.members.find(member => member.portalMemberId === row.reference)?.fullName || `Miembro ${row.reference}`; }
  destinationName(row: PhotoRow) { return this.unitOptions.find(unit => unit.id === row.selection.unitId)?.name || (row.selection.unitId ? 'Comunidad seleccionada' : 'Elegir comunidad'); }
  canSelect(row: PhotoRow) { return !row.error && !row.consumed && !row.loading && this.memberIncluded(row) && (!row.isUnit || !!row.state); }
  private selectableGroup(isUnit: boolean) { return this.rows().filter(row => row.isUnit === isUnit && this.canSelect(row)); }
  groupSelected(isUnit: boolean) { const rows = this.selectableGroup(isUnit); return rows.length > 0 && rows.every(row => row.selection.include); }
  groupPartial(isUnit: boolean) { const rows = this.selectableGroup(isUnit); return rows.some(row => row.selection.include) && !rows.every(row => row.selection.include); }
  groupDisabled(isUnit: boolean) { return !this.selectableGroup(isUnit).length || this.rows().some(row => row.isUnit === isUnit && row.loading); }
  groupHasReplacements(isUnit: boolean) { return !isUnit && this.rows().some(row => !row.isUnit && !!row.state?.photos.length && this.canSelect(row)); }
  toggleGroup(isUnit: boolean, include: boolean) {
    for (const row of this.selectableGroup(isUnit)) row.selection.include = include;
    this.rows.update(rows => [...rows]); this.emit();
  }
  private generation = 0;
  private previewLoading = false;
  private readonly previewUrls = new Set<string>();
  private clearPreviews() { this.previewUrls.forEach(url => URL.revokeObjectURL(url)); this.previewUrls.clear(); }
  ngOnDestroy() { ++this.generation; this.clearPreviews(); }
  async ngOnChanges(changes: SimpleChanges) {
    if (!changes['batchId']) { await this.applyUnitDestinations(); this.emit(); return; }
    const generation = ++this.generation;
    this.clearPreviews();
    this.activePhotoId.set(null);
    this.previewLoading = !!this.batchId;
    this.rows.set([]); this.error.set(''); this.emit();
    if (!this.batchId) return;
    try {
      const preview = await firstValueFrom(this.http.get<PortalPhotoPreview[]>(`${environment.apiUrl}/portal-member-imports/${this.batchId}/photos`));
      const rows: PhotoRow[] = preview.map(p => ({ ...p, selection: { assetId: p.id, include: !p.isUnit && !p.error && !p.consumed, revision: '00000000-0000-0000-0000-000000000000' } }));
      for (const row of rows.filter(r => r.personId && !r.error && !r.consumed)) {
        try {
          row.state = await this.api.state(row.personId!);
          if (!row.state.canManage) { row.error = 'No tenés permiso para importar la foto de este miembro.'; row.selection.include = false; continue; }
          row.selection.revision = row.state.revision; row.selection.replacePhotoId = row.state.photos[0]?.id;
          row.selection.include = row.state.photos.length === 0;
        }
        catch { row.error = 'No se pudo verificar el permiso y la foto actual del miembro.'; row.selection.include = false; }
      }
      if (generation !== this.generation) return;
      this.rows.set(rows);
      await this.applyUnitDestinations();
      this.emit();
    } catch (e) { if (generation === this.generation) this.error.set(feedbackError(e)); }
    finally { if (generation === this.generation) { this.previewLoading = false; this.emit(); } }
  }
  private async applyUnitDestinations() {
    for (const row of this.rows().filter(r => r.isUnit && !r.error && !r.consumed)) {
      const reference = row.reference.trim().toLowerCase();
      const inferred = this.unitDestinations[`portal:${reference}`] ?? this.unitDestinations[reference];
      if (row.selection.unitId && row.selection.unitId !== row.inferredUnitId) continue;
      row.inferredUnitId = inferred;
      if (row.selection.unitId === inferred) continue;
      row.selection.unitId = inferred;
      await this.destination(row);
    }
  }
  memberIncluded(row: PhotoRow) { return row.isUnit || this.includedMemberReferences === null || this.includedMemberReferences.includes(row.reference); }
  async destination(row: PhotoRow) {
    const id = row.selection.unitId;
    const version = row.destinationVersion = (row.destinationVersion ?? 0) + 1;
    row.state = undefined; row.selection.include = false; row.selection.replacePhotoId = undefined;
    row.destinationError = undefined;
    row.loading = true; this.emit();
    try {
      if (id) {
        const state = await this.api.state(id, true);
        if (version !== row.destinationVersion) return;
        if (!state.canManage) throw new Error();
        row.state = state; row.selection.revision = state.revision;
        row.selection.include = true;
      }
    } catch { if (version === row.destinationVersion) row.destinationError = 'No se pudieron consultar las fotos de la comunidad elegida. Revisá los permisos.'; }
    finally { if (version === row.destinationVersion) { row.loading = false; this.rows.update(r => [...r]); this.emit(); } }
  }
  async crop(row: PhotoRow) {
    const generation = this.generation;
    row.loading = true; this.rows.update(r => [...r]); this.emit();
    try {
      const blob = await firstValueFrom(this.http.get(`${environment.apiUrl}/portal-member-imports/photos/${row.id}?variant=main`, { responseType: 'blob' }));
      const { PhotoCropDialogComponent } = await import('@shared/components/entity-photos/photo-crop-dialog.component');
      const result: PhotoCropPreviewResult | undefined = await firstValueFrom(this.dialog.open(PhotoCropDialogComponent, { data: { file: new File([blob], 'portal.jpg', { type: 'image/jpeg' }), unit: row.isUnit, returnPreview: true }, width: '680px' }).afterClosed());
      if (result && generation === this.generation) {
        const previewUrl = URL.createObjectURL(result.previewBlob);
        if (row.croppedThumbnailUrl) { URL.revokeObjectURL(row.croppedThumbnailUrl); this.previewUrls.delete(row.croppedThumbnailUrl); }
        row.croppedThumbnailUrl = previewUrl; this.previewUrls.add(previewUrl);
        row.selection.crop = result.crop; this.emit();
      }
    } catch (e) { this.error.set(feedbackError(e)); }
    finally { row.loading = false; this.rows.update(r => [...r]); this.emit(); }
  }
  emit() {
    this.loadingChange.emit(this.previewLoading || this.rows().some(row => !!row.loading));
    this.selectionsChange.emit(this.rows().map(row => ({ ...row.selection, include: row.selection.include && this.memberIncluded(row) && !row.error && !row.consumed && !row.loading })));
  }
}
